// src/components/BusinessConsentList.jsx
import React, { useState, useEffect } from 'react';
import { consentService } from '../services/consentService';
import { Shield, Calendar, Clock, CheckCircle, User } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import styles from './BusinessConsentList.module.css';

const SCOPE_LABELS = {
  'availability:read:freebusy': 'View Free/Busy Times',
  'availability:read:details': 'View Detailed Availability',
  'booking:propose': 'Propose Appointments'
};

export default function BusinessConsentList({ userId }) {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const loadConsents = async () => {
      try {
        setLoading(true); setError(null);
        const { data, error: fetchError } = await consentService.getConsents('grantee', true);
        if (cancelled) return;
        if (fetchError) throw fetchError;
        setConsents(data || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading consents:', err);
        setError(err.message || 'Failed to load access permissions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadConsents();
    return () => { cancelled = true; };
  }, [userId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const daysUntilExpiry = (new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <LoadingSpinner size="md" text="Loading access permissions..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorBox}>
        <p className={styles.errorHeading}>Error</p>
        <p className={styles.errorText}>{error}</p>
      </div>
    );
  }

  if (consents.length === 0) {
    return (
      <EmptyState
        icon={Shield}
        title="No access permissions yet"
        description="When customers grant you access to view their availability, those permissions will appear here."
      />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Shield className={styles.headerIcon} size={24} aria-hidden="true" />
        <div>
          <h3 className={styles.headerTitle}>Customer Access Permissions</h3>
          <p className={styles.headerSubtitle}>View the permissions customers have granted you</p>
        </div>
      </div>

      <div className={styles.list}>
        {consents.map((consent) => (
          <div key={consent.id} className={styles.consentCard}>
            <div className={styles.consentTop}>
              <User size={18} className={styles.userIcon} aria-hidden="true" />
              <h4 className={styles.consentName}>
                {consent.grantor?.full_name || consent.grantor?.email || 'Customer'}
              </h4>
              {isExpiringSoon(consent.expires_at) && (
                <span className={styles.expiringBadge}>
                  <Clock size={12} aria-hidden="true" />
                  Expiring Soon
                </span>
              )}
            </div>

            {consent.grantor?.email && (
              <p className={styles.email}>{consent.grantor.email}</p>
            )}

            <div className={styles.scopeList}>
              {consent.scopes?.map((scope) => (
                <span key={scope} className={styles.scopeTag}>
                  <CheckCircle size={12} aria-hidden="true" />
                  {SCOPE_LABELS[scope] || scope}
                </span>
              ))}
            </div>

            <div className={styles.meta}>
              <span className={styles.metaItem}>
                <Calendar size={12} aria-hidden="true" />
                Granted {formatDate(consent.created_at)}
              </span>
              {consent.expires_at ? (
                <span className={styles.metaItem}>
                  <Clock size={12} aria-hidden="true" />
                  Expires {formatDate(consent.expires_at)}
                </span>
              ) : (
                <span className={styles.noExpiry}>No expiration</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
